import { Badge } from '#client/components/ui/Badge';
import { PaymentStatusBadge } from '#client/components/ui/StatusBadge';
import type { Column } from '#client/components/ui/DataTable';
import { Identifier } from '#client/components/ui/Identifier';
import { ResourceListPage } from '#client/components/ui/ResourceListPage';
import { formatAge, formatAgeShort, formatMoney } from '#client/lib/format';
import { ENUM_LABELS, type PatientListItem, PIN_PREFIX } from '#shared/types';
import { RESOURCE } from '#client/lib/routes';

const columns: Column<PatientListItem>[] = [
  {
    key: 'pin_no',
    header: 'PIN',
    // Frozen, so the identifier stays visible while scrolling sideways — it is
    // the only thing that tells an operator which row they are reading. The
    // `pin_` prefix is dropped on screen and kept on copy.
    frozen: true,
    render: (row) => <Identifier value={row.pin_no} stripPrefix={PIN_PREFIX} />,
    // Wide enough for `2026-08-07-265456` in the mono face without wrapping.
    width: '176px',
    skeletonWidth: '120px',
  },
  { key: 'name', header: 'Name', render: (row) => row.name, skeletonWidth: '9rem' },
  {
    key: 'age',
    header: 'Age',
    // The compact form; the full one is on the patient detail screen.
    render: (row) => (
      <span className="tnum" title={formatAge(row.age_year, row.age_month, row.age_days)}>
        {formatAgeShort(row.age_year, row.age_month, row.age_days)}
      </span>
    ),
    width: '90px',
    skeletonWidth: '3.5rem',
  },
  { key: 'gender', header: 'Gender', render: (row) => ENUM_LABELS.gender[row.gender] },
  {
    key: 'phone_number',
    header: 'Phone',
    render: (row) => <span className="tnum">{row.phone_number}</span>,
  },
  {
    key: 'reports',
    header: 'Reports',
    render: (row) => <Badge tone="neutral">{row.reports.length}</Badge>,
    align: 'center',
  },
  {
    key: 'payment_status',
    header: 'Payment',
    render: (row) => <PaymentStatusBadge status={row.payment_status} />,
  },
  {
    key: 'total',
    header: 'Total',
    render: (row) => formatMoney(row.total),
    align: 'end',
    width: '140px',
  },
];

export function PatientListPage() {
  return (
    <ResourceListPage<PatientListItem>
      title="Patients"
      subtitle="Registered patients and their reports"
      path={RESOURCE.patients}
      columns={columns}
      noun="patient"
      nounPlural="patients"
      searchPlaceholder="Search by name, PIN, MR number or phone"
      rowLabel={(row) => `${row.name} (${row.pin_no})`}
      createCapability="patient.create"
      editCapability="patient.edit"
      deleteCapability="patient.delete"
    />
  );
}
